import { useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { OWNER } from "constants/index"
import { useAppSelector } from "appRedux/hooks"
import { updateCurrentItemInfo } from "appRedux/slices/wishListSlice"
import { useLazyGetUserWishItemQuery, userProfileApi } from "appRedux/slices/userApiSlice"
import { useLazyGetWishItemQuery, wishItemApi } from "appRedux/slices/wishItemApiSlice"
import { isItemViewPage } from "utils/navigatorUtils"

export default function useWishItem(itemId?: number | null) {
  const role = useAppSelector((state) => state.global.role)
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const wishListState = useAppSelector((state) => state.wishList)

  const id = useMemo(() => {
    return itemId ? itemId : (currentPageInfo?.dynamicPathParts?.itemId ? currentPageInfo?.dynamicPathParts?.itemId : wishListState.itemInfo?.id)
  }, [itemId, currentPageInfo?.dynamicPathParts?.itemId, wishListState.itemInfo?.id])

  const cachedWishItem = useAppSelector((state) => {
    if (!id) return
    if (role === OWNER) {
      return wishItemApi.endpoints.getWishItem.select(Number(id))(state)
    } else {
      return currentPageInfo?.dynamicPathParts?.handle ? userProfileApi.endpoints.getUserWishItem.select({
        handle: currentPageInfo?.dynamicPathParts?.handle,
        id: Number(id)
      })(state) : null
    }
  })

  const [triggerGetWishItem, loggedInUserWishItemResult] = useLazyGetWishItemQuery()
  const [triggerGetUserWishItem, viewedUserWishItemResult] = useLazyGetUserWishItemQuery()

  const dispatch = useDispatch()

  useEffect(() => {
    if (!currentPageInfo?.dynamicPathParts?.handle) return
    if (!isItemViewPage(currentPageInfo?.path)) return
    if (cachedWishItem?.data) {
      dispatch(updateCurrentItemInfo(cachedWishItem.data))
      return
    }
    fetchWishItem()
  }, [role, wishListState.itemInfo?.id, itemId, JSON.stringify(currentPageInfo?.dynamicPathParts)])

  function fetchWishItem() {
    if (!id) return

    if (role === OWNER) {
      triggerGetWishItem(Number(id))
    } else if (currentPageInfo?.dynamicPathParts?.handle) {
      triggerGetUserWishItem({ handle: currentPageInfo?.dynamicPathParts?.handle, id: Number(id) })
    }
  }

  return {
    wishItem: cachedWishItem?.data || loggedInUserWishItemResult.data || viewedUserWishItemResult.data,
    isFetching: role === OWNER ? loggedInUserWishItemResult.isFetching : viewedUserWishItemResult.isFetching,
    isError: role === OWNER ? loggedInUserWishItemResult.isError : viewedUserWishItemResult.isError
  }
}
